import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // 15 second timeout (reduced to fail faster)
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('[API] No auth token available');
      }
      // Don't set Content-Type for multipart/form-data (let browser set it with boundary)
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }
    } catch (error) {
      console.error('[API] Failed to get auth token:', error);
    }
    return config;
  },
  (error) => {
    console.error('[API] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('[API] Response error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      code: error.code
    });
    
    if (error.response?.status === 401) {
      // Token expired or invalid
      // Don't redirect if we're already on login or signup page
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/signup') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  verify: () => api.post('/auth/verify'),
  getMe: () => api.get('/auth/me'),
  checkEmail: (email) => axios.post(`${API_URL}/auth/check-email`, { email }),
};

// User APIs
export const userAPI = {
  getUser: (userId) => api.get(`/users/${userId}`),
  updateUser: (userId, data) => api.put(`/users/${userId}`, data),
  submitQuestionnaire: (userId, answers, sport) => api.post(`/users/${userId}/questionnaire`, { answers, sport }),
  getUserLeagues: (userId) => api.get(`/users/${userId}/leagues`),
  getUserStats: (userId) => api.get(`/users/${userId}/stats`),
  getUserMatches: (userId) => api.get(`/users/${userId}/matches`),
  getCreatedLeagues: (userId) => api.get(`/users/${userId}/created-leagues`),
};

// League APIs
export const leagueAPI = {
  /**
   * Get leagues near a location using geohash-based queries or map bounds
   * @param {number} lat - Latitude of search center
   * @param {number} lng - Longitude of search center
   * @param {number} radius - Search radius in km (default: 10)
   * @param {number} limit - Number of items per page (default: 50)
   * @param {string} exclusiveStartKey - Pagination token for next page
   * @param {object} filters - Optional filters: { sport, dateFilter, minLevel, maxLevel, leagueTypeFilter, clubFilter }
   * @param {object} bounds - Optional map bounds: { swLat, swLng, neLat, neLng }
   */
  getAllLeagues: (lat, lng, radius, limit, exclusiveStartKey, filters = {}, bounds = null) => {
    const params = {};
    if (lat != null) params.lat = lat;
    if (lng != null) params.lng = lng;
    if (radius != null) params.radius = radius;
    if (limit) params.limit = limit;
    if (exclusiveStartKey) params.exclusiveStartKey = exclusiveStartKey;
    if (filters.sport) params.sport = filters.sport;
    if (filters.dateFilter) params.dateFilter = filters.dateFilter;
    if (filters.minLevel !== undefined && filters.minLevel !== null && filters.minLevel !== '') {
      params.minLevel = filters.minLevel;
    }
    if (filters.maxLevel !== undefined && filters.maxLevel !== null && filters.maxLevel !== '') {
      params.maxLevel = filters.maxLevel;
    }
    if (filters.leagueTypeFilter) params.leagueTypeFilter = filters.leagueTypeFilter;
    if (filters.clubFilter) params.clubFilter = filters.clubFilter;
    
    // Add map bounds if provided (more accurate than center + radius)
    if (bounds && bounds.swLat != null && bounds.swLng != null && bounds.neLat != null && bounds.neLng != null) {
      params.swLat = bounds.swLat;
      params.swLng = bounds.swLng;
      params.neLat = bounds.neLat;
      params.neLng = bounds.neLng;
    }
    
    return api.get('/leagues', { params });
  },
  getLeague: (leagueId, options = {}) => {
    const params = {};
    if (options.includeMatches) params.includeMatches = 'true';
    if (options.matchLimit) params.matchLimit = options.matchLimit;
    if (options.matchExclusiveStartKey) params.matchExclusiveStartKey = options.matchExclusiveStartKey;
    return api.get(`/leagues/${leagueId}`, { params });
  },
  getSuperLeague: (leagueId) => api.get(`/leagues/super/${leagueId}`),
  // DEPRECATED: Use interestedUsersAPI.getUserInterestedLeagues instead
  // Kept for backward compatibility during transition
  getInterestedLeagues: () => api.get('/leagues/interested/me'),
  // DEPRECATED: Use interestedUsersAPI.getUserInterestedLeagues instead
  // Kept for backward compatibility during transition
  getInterestedLeaguesByUser: (userId) => api.get(`/leagues/interested/${userId}`),
  createLeague: (data) => api.post('/leagues', data),
  updateLeague: (leagueId, data) => api.put(`/leagues/${leagueId}`, data),
  deleteLeague: (leagueId) => api.delete(`/leagues/${leagueId}`),
  expressInterest: (leagueId) => api.post(`/leagues/${leagueId}/interest`),
  removeInterest: (leagueId) => api.delete(`/leagues/${leagueId}/interest`),
  acceptUsers: (leagueId, userIds) => api.post(`/leagues/${leagueId}/accept`, { userIds }),
  rejectUser: (leagueId, userId) => api.post(`/leagues/${leagueId}/reject`, { userId }),
  removePlayer: (leagueId, userId) => api.delete(`/leagues/${leagueId}/players/${userId}`),
  createContinuationLeague: (leagueId) => api.post(`/leagues/${leagueId}/continuation`),
  /**
   * Get paginated members for a league (admin only)
   * @param {string} leagueId 
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 50, max: 100)
   * @param {string} type - 'players' | 'pending' | 'rejected'
   */
  getLeagueMembers: (leagueId, page = 1, limit = 50, type = 'players') => {
    const params = { page, limit, type };
    return api.get(`/leagues/${leagueId}/members`, { params });
  },
};

// Match APIs
export const matchAPI = {
  addMatch: (data) => api.post('/matches', data),
  getLeagueMatches: (leagueId) => api.get(`/matches/${leagueId}`),
  updateMatch: (matchId, data) => api.put(`/matches/${matchId}`, data),
  deleteMatch: (matchId, leagueId) => api.delete(`/matches/${matchId}?leagueId=${leagueId}`),
};

// Interested Users APIs (NEW - for scalability)
export const interestedUsersAPI = {
  /**
   * Get interested users for a league (admin only, with pagination)
   * @param {string} leagueId 
   * @param {number} limit - Number of results per page (default: 50, max: 100)
   * @param {string} nextToken - Pagination token for next page
   * @param {string} status - Optional filter: 'pending', 'approved', 'rejected'
   */
  getInterestedUsers: (leagueId, limit, nextToken, status) => {
    const params = { limit: limit || 50 };
    if (nextToken) params.exclusiveStartKey = nextToken;
    if (status) params.status = status;
    return api.get(`/interested-users/league/${leagueId}`, { params });
  },
  
  /**
   * Get leagues user is interested in (with pagination)
   * @param {string} userId 
   * @param {number} limit - Number of results per page (default: 50, max: 100)
   * @param {string} nextToken - Pagination token for next page
   * @param {string} status - Optional filter: 'pending', 'approved', 'rejected'
   */
  getUserInterestedLeagues: (userId, limit, nextToken, status) => {
    const params = { limit: limit || 50 };
    if (nextToken) params.exclusiveStartKey = nextToken;
    if (status) params.status = status;
    return api.get(`/interested-users/user/${userId}`, { params });
  },
  
  /**
   * Express interest in a league
   * @param {string} leagueId 
   */
  expressInterest: (leagueId) => api.post(`/interested-users/${leagueId}`),
  
  /**
   * Remove interest from a league
   * @param {string} leagueId 
   */
  removeInterest: (leagueId) => api.delete(`/interested-users/${leagueId}`),
  
  /**
   * Update status of an interested user (admin only)
   * @param {string} leagueId 
   * @param {string} userId 
   * @param {string} status - 'pending', 'approved', 'rejected'
   */
  updateInterestedUserStatus: (leagueId, userId, status) => 
    api.put(`/interested-users/${leagueId}/${userId}/status`, { status }),
};

// Levels APIs
export const levelsAPI = {
  getUserLevels: (userId, sport) => api.get(`/levels/${userId}`, { params: { sport } }),
  checkUserLevel: (userId, sport) => api.get(`/levels/${userId}/current`, { params: { sport } }),
};

// Admin APIs
export const adminAPI = {
  getAllUsers: () => api.get('/admin/users'),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  removeUserFromLeague: (leagueId, userId) => api.post(`/admin/leagues/${leagueId}/remove-user`, { userId }),
};

// Upload APIs
export const uploadAPI = {
  uploadProfilePhoto: (file) => {
    const formData = new FormData();
    formData.append('photo', file);
    return api.post('/upload/profile-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadLeagueLogo: (file, leagueId) => {
    const formData = new FormData();
    formData.append('logo', file);
    formData.append('leagueId', leagueId);
    return api.post('/upload/league-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadClubLogo: (file, clubId) => {
    const formData = new FormData();
    formData.append('logo', file);
    formData.append('clubId', clubId);
    return api.post('/upload/club-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Support APIs
export const supportAPI = {
  submitSupport: (formData) => {
    return api.post('/support', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // 60 seconds for file uploads
    });
  },
};

// Club APIs
export const clubAPI = {
  getAllClubs: () => api.get('/clubs'),
  getClub: (clubId) => api.get(`/clubs/${clubId}`),
  getMyClubs: () => api.get('/clubs/my-clubs'),
  /**
   * Get club members with batch loading (scalable)
   * @param {string} clubId - The club ID
   * @param {number} limit - Number of members to fetch (default: 50, max: 100)
   * @param {string} nextToken - Pagination token for next page
   * @returns {Promise} Response with members array and pagination info
   */
  getClubMembers: (clubId, limit, nextToken) => {
    const params = { limit: limit || 50 };
    if (nextToken) params.exclusiveStartKey = nextToken;
    return api.get(`/clubs/${clubId}/members`, { params });
  },
  createClub: (data) => api.post('/clubs', data),
  updateClub: (clubId, data) => api.put(`/clubs/${clubId}`, data),
  deleteClub: (clubId) => api.delete(`/clubs/${clubId}`),
  addMember: (clubId, email) => api.post(`/clubs/${clubId}/members`, { email }),
  removeMember: (clubId, userId) => api.delete(`/clubs/${clubId}/members/${userId}`),
};

// Message APIs
export const messageAPI = {
  sendMessage: (receiverId, content) => 
    api.post('/messages', { receiverId, content }),
  getConversations: (limit, nextToken) => {
    const params = {};
    if (limit) params.limit = limit;
    if (nextToken) params.nextToken = nextToken;
    return api.get('/messages/conversations', { params });
  },
  getConversation: (otherUserId, limit, before, nextToken) => {
    const params = {};
    if (limit) params.limit = limit;
    if (before) params.before = before;
    if (nextToken) params.nextToken = nextToken;
    return api.get(`/messages/conversation/${otherUserId}`, { params });
  },
  markConversationRead: (otherUserId, conversationId) => {
    const params = conversationId ? { conversationId } : {};
    return api.put(`/messages/conversation/${otherUserId}/read-all`, {}, { params });
  },
  getUnreadCount: () => api.get('/messages/unread-count'),
  canMessage: (userId) => api.get(`/messages/can-message/${userId}`),
};

// Health APIs
export const healthAPI = {
  check: () => api.get('/health'),
};

export default api;

